import { DateTime } from 'luxon';
import type { ResolvedDeliveryDate } from '../../delivery-date/resolve-delivery-date.js';
import { BERLIN } from '../../delivery-date/working-days.js';
import type { ExtractedProductInfo } from '../../information-extraction/db/ExtractedProductInfo.js';
import { ManualCorrectedProductInfo } from '../../information-extraction/db/ExtractedProductInfo.js';
import type { MailExtractionResult } from '../../information-extraction/db/MailExtractionResult.js';

/**
 * Ein Datumsfeld als `yyyy-MM-dd`, egal in welcher Gestalt es ankommt.
 *
 * Ein Feld vom Typ `DateTime` ist nach dem Weg durch die Datenbank je nach Aufrufer ein
 * Luxon-Objekt (frische Extraktion) oder ein ISO-String (aus Mongo gelesen). Und weil der Server
 * in UTC läuft, muss vor dem Formatieren zwingend nach Berlin gezont werden — sonst wird aus
 * Mitternacht des 21.09. der 20.09.
 */
export function toDayString(value: unknown): string | null {
  if (!value) return null;
  const dateTime =
    value instanceof DateTime
      ? value
      : typeof value === 'string'
        ? DateTime.fromISO(value)
        : value instanceof Date
          ? DateTime.fromJSDate(value)
          : null;
  if (!dateTime?.isValid) return null;
  return dateTime.setZone(BERLIN).toFormat('yyyy-MM-dd');
}

/**
 * Der von Hand gesetzte Liefertermin einer Position — oder `null`, wenn der Termin nur so in
 * der Anfrage stand.
 *
 * Der Unterschied ist der ganze Punkt: Was die KI aus dem Dokument gelesen hat, ersetzt der
 * Routenplaner; was ein Mensch eingetragen oder auf eine Rückfrage geantwortet hat, bleibt
 * stehen. Eine neu angelegte Position ist immer Handarbeit; bei einer korrigierten zählt, ob
 * sich das Datum gegenüber dem extrahierten Original geändert hat.
 */
export function manualPositionDate(result: MailExtractionResult, product: ExtractedProductInfo): string | null {
  if (!(product instanceof ManualCorrectedProductInfo)) return null;

  const corrected = toDayString(product.desiredDeliveryDate);
  if (!corrected) return null;
  if (product.isNewProduct) return corrected;

  const original = result.extractedProducts?.find(p => p._id === product.extractedProductInfoId);
  const extracted = toDayString(original?.desiredDeliveryDate);
  return corrected === extracted ? null : corrected;
}

export interface ExportDeliveryDates {
  /** REQ_DATE_H — `yyyy-MM-dd`. */
  headerDate: string;
  /** REQ_DATE der Position — die Handeingabe der Position, sonst das Kopfdatum. */
  dateForPosition: (product: ExtractedProductInfo) => string;
}

/**
 * Welche Termine in den SAP-Export gehen.
 *
 * Vorrang: was ein Mensch gesetzt hat, dann das berechnete Datum. Der vom Kunden in der Anfrage
 * genannte Termin steht bewusst nicht in dieser Kette — er bleibt im Extraktionsergebnis
 * sichtbar, bestimmt aber nicht mehr, was H&D zusagt.
 */
export function resolveExportDeliveryDates(result: MailExtractionResult, plannedDate: string): ExportDeliveryDates {
  const headerDate = result.manualDeliveryDate ?? plannedDate;
  return {
    headerDate,
    dateForPosition: product => manualPositionDate(result, product) ?? headerDate,
  };
}

/**
 * Sorgt dafür, dass am Ergebnis ein Termin steht, der nicht verstrichen ist — und gibt ihn
 * zurück.
 *
 * Ein Angebot kann Tage nach der Extraktion freigegeben oder von Hand erneut gesendet werden;
 * der damals errechnete Termin liegt dann womöglich hinter uns. Das Ergebnis wird dabei
 * bewusst mitgeschrieben: Sonst stünde in der Oberfläche der alte Termin und im XML ein
 * anderer — genau die Situation, die es beim Längenfenster schon einmal gab.
 *
 * `neuRechnen` wird nur aufgerufen, wenn es nötig ist; so bleibt der Normalfall ohne
 * Datenbankzugriff.
 */
export async function ensureCurrentPlannedDate(
  result: MailExtractionResult,
  heute: string,
  neuRechnen: () => Promise<ResolvedDeliveryDate>,
): Promise<string> {
  const gespeichert = result.plannedDeliveryDate;
  if (gespeichert && gespeichert >= heute) return gespeichert;

  const neu = await neuRechnen();
  result.plannedDeliveryDate = neu.date;
  result.deliveryDateBasis = neu.basis;
  return neu.date;
}
